import { Button, ButtonText } from '@/components/ui/button'
import { Center } from '@/components/ui/center'
import { Text } from '@/components/ui/text'
import UniversalWebView from '@/components/UniversalWebView'
import type { OpenLinkResult, UniversalWebViewRef } from '@/components/universalWebView/types'
import useDefaultToast from '@/components/useDefaultToast'
import { getAuthToken } from '@/lib/api/authToken'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import saveImageToLibrary from '@/lib/utils/saveImageToLibrary'
import * as Sentry from '@sentry/react-native'
import { router, useNavigation, useRoute } from 'expo-router'
import { CommonActions } from 'expo-router/react-navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Linking, Platform, StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { artifactDownloadUrl, artifactPageToken } from '@/modules/artifacts/artifactUrl'
import useExportFile from '@/modules/artifacts/useExportFile'
import { CHAT_CAPS, handleChatPurchase, type ChatPurchase } from '@/modules/handleChatPurchase'
import { useAppSelector } from '@/redux/store'

import { clearJiriDraft, hasJiriDraftInUrl, readJiriDraft } from './jiriDraft'

const BRIDGE_MESSAGE_SOURCE = 'cs-chat-sdk'

const JIRI_WEB_BASE_URL = process.env.EXPO_PUBLIC_JIRI_WEB_BASE_URL

const NEXT_BASE_URL = process.env.EXPO_PUBLIC_NEXT_BASE_URL

const DUPLICATE_CLICK_MS = 1000

// The App's WebView, on iOS and Android alike, receives `openObject`, `saveImage` and `downloadArtifact` over
// ReactNativeWebView: its WebView can neither download a file nor be trusted to open the editor link in place.
// An iframe has no such trouble saving, so the browser keeps its own download.
const HOST_CAPS = Platform.OS === 'web' ? ['openObject'] : ['openObject', 'saveImage', 'downloadArtifact']
const CAPS = [CHAT_CAPS, ...HOST_CAPS].filter(Boolean).join(',')

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,'

type OpenObject = { kind?: 'form' | 'table' | 'query'; token?: string; url?: string }

type SaveImage = { dataUrl?: string; filename?: string }

type DownloadArtifact = { token?: string; name?: string }

type ConversationChanged = { conversationId?: string | null; reason?: 'opened' | 'created' | 'draft' }

type JiriBridgeMessage = { source?: string; type?: string; id?: string; data?: unknown }

function buildChatUrl(conversationId: string | undefined): string | null {
  if (!JIRI_WEB_BASE_URL) return null
  // The App's long-lived token, as the nextfe `/m/*` embeds carry it. jiri-web keeps `m_t` for the life of the
  // page and goldendata enforces a JWT's expiry, so the five-minute one from `jwt_oauth/new` signed a reader
  // with no main-site cookie (the App, a miniprogram web-view) out in the middle of a conversation.
  const token = getAuthToken()
  if (!token) return null

  const path = conversationId ? `/chat/${encodeURIComponent(conversationId)}` : '/chat'
  const chatUrl = new URL(path, JIRI_WEB_BASE_URL)
  chatUrl.searchParams.set('utm_device', 'mobile')
  chatUrl.searchParams.set('utm_source', 'gd_app')
  chatUrl.searchParams.set('utm_medium', 'nextapp')
  chatUrl.searchParams.set('m_t', token)
  if (CAPS) chatUrl.searchParams.set('caps', CAPS)
  return chatUrl.toString()
}

function openObject({ kind, token, url }: OpenObject) {
  if (kind === 'form' && token) {
    router.push(`/forms/${encodeURIComponent(token)}/edit`)
    return
  }
  if (kind === 'table' && token) {
    router.push(`/tables/${encodeURIComponent(token)}`)
    return
  }
  // A query has no screen here; its nextfe page is where jiri-web would have sent the reader itself. Opened
  // while the click in the frame still counts as the reader's, so the new tab is not blocked as a popup.
  if (!url || !/^https?:\/\//i.test(url)) return
  if (Platform.OS === 'web') window.open(url, '_blank', 'noopener')
  else Linking.openURL(url).catch(() => {})
}

function close() {
  if (router.canGoBack()) router.back()
  else router.replace('/forms')
}

/**
 * Jiri is jiri-web in a WebView on every platform, App and mobile web alike. eve is not stable yet and its
 * clients have to move with it; a store release cannot keep that pace, and jiri cannot serve every App
 * version still installed, so the chat stays on the side that deploys with jiri.
 */
export default function JiriWebView({ conversationId }: { conversationId?: string }) {
  // The page loads once: jiri-web moves between conversations inside it, and rebuilding the address from the
  // `conversationId` written back below would reload it under the reader.
  const [initialConversationId] = useState(conversationId)
  const [url, setUrl] = useState(() => buildChatUrl(initialConversationId))
  // What the main site's hero input handed over, to open the blank thread with. A conversation link takes none.
  const [handoffDraft] = useState(() => (initialConversationId ? null : readJiriDraft()))
  const handoffRef = useRef<'pending' | 'sent' | 'done'>('pending')
  const [ready, setReady] = useState(false)
  const jiriMobile = useAppSelector(state => state.session.jiriMobile)
  const webViewRef = useRef<UniversalWebViewRef>(null)
  const [secondaryLabel] = usePlatformColor('secondaryLabel')
  const insets = useSafeAreaInsets()
  const toast = useDefaultToast()
  const exportFile = useExportFile()
  const lastArtifactOpen = useRef<{ token: string; at: number } | null>(null)

  // A file Jiri delivered opens on a screen of its own, the chat kept behind it; every other link leaves the App.
  const openLink = useCallback((url: string): OpenLinkResult => {
    const token = artifactPageToken(url, NEXT_BASE_URL)
    if (!token) return 'browser'
    // iOS can report one click twice; the second would stack the same file again.
    const now = Date.now()
    const last = lastArtifactOpen.current
    if (last?.token !== token || now - last.at > DUPLICATE_CLICK_MS) {
      lastArtifactOpen.current = { token, at: now }
      router.push({ pathname: '/artifacts/[token]', params: { token } })
    }
    return 'handled'
  }, [])

  const saveImage = async ({ dataUrl, filename }: SaveImage) => {
    if (!dataUrl?.startsWith(PNG_DATA_URL_PREFIX)) return
    // The title becomes a path segment, so a `/` would point the write at a directory that does not exist. A
    // Chinese character is three bytes and a file name has 255, so a long title would fail the write too.
    const title = (filename ?? '').replace(/\.png$/i, '').replace(/[\\/:*?"<>|]/g, '_')
    const name = Array.from(title).slice(0, 50).join('') || 'jiri'
    const result = await saveImageToLibrary({
      filename: `${name}-${Date.now()}`,
      base64: dataUrl.slice(PNG_DATA_URL_PREFIX.length),
    })
    if (result.success) toast.show('已保存到相册')
    else if (result.error === 'permission-denied') toast.show('请在系统设置中开启相册权限')
    else toast.show('保存图片失败')
  }

  // Built from the token alone, like the artifact screen's page: the `url` jiri-web sends along is not trusted.
  const downloadArtifact = ({ token, name }: DownloadArtifact) => {
    const url = artifactDownloadUrl(token, NEXT_BASE_URL, getAuthToken())
    if (url) exportFile(url, typeof name === 'string' ? name : '')
  }

  const navigation = useNavigation()
  const route = useRoute()

  // Out of the address bar: expo-router keeps the fragment as a route param and writes it back on every change.
  // Dropped from this screen's own params once the Stack mounted with it has committed: before that the Stack
  // writes back the state it rendered, and `router.setParams` lands on the (system) route, where an `undefined`
  // comes out as `#undefined` on every page after.
  useEffect(() => {
    if (!hasJiriDraftInUrl()) return
    const timer = setTimeout(() => {
      const params: Record<string, unknown> = { ...route.params }
      delete params['#']
      navigation.dispatch(CommonActions.replaceParams(params))
    })
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gated like the entry points are, so a handoff cannot open Jiri for an account it is not open to yet
  useEffect(() => {
    if (!ready || !jiriMobile || !handoffDraft || handoffRef.current !== 'pending') return
    handoffRef.current = 'sent'
    webViewRef.current?.postMessage({
      source: BRIDGE_MESSAGE_SOURCE,
      type: 'sendUserMessage',
      id: 'nextapp-jiri-draft',
      data: handoffDraft,
    })
  }, [ready, jiriMobile, handoffDraft])

  useEffect(() => {
    if (url) return
    // Rendering nothing here looked like a blank page. The usual cause is a signed-out session.
    Sentry.captureMessage('Jiri: no chat URL', { extra: { hasBaseUrl: !!JIRI_WEB_BASE_URL } })
  }, [url])

  if (!url) {
    return (
      <Center className="flex-1 px-8">
        <Text size="lg" className="font-semibold">
          Jiri 加载失败
        </Text>
        <Text size="md" style={{ color: secondaryLabel }} className="mt-2 text-center">
          请稍后重试；如果一直失败，请退出登录后重新登录
        </Text>
        <Button size="lg" className="mt-8 w-full max-w-sm" onPress={() => setUrl(buildChatUrl(initialConversationId))}>
          <ButtonText>重试</ButtonText>
        </Button>
      </Center>
    )
  }

  return (
    // env(safe-area-inset-bottom) reads 0 inside an iframe and the Android WebView, so the room is left here.
    <View style={[styles.container, Platform.OS !== 'ios' && { paddingBottom: insets.bottom }]}>
      <UniversalWebView<JiriBridgeMessage>
        ref={webViewRef}
        source={{ uri: url }}
        style={styles.webview}
        allow="clipboard-write"
        onOpenLink={openLink}
        onMessage={message => {
          // Must-ignore: a bridge type this build does not know is not an error.
          if (message?.source !== BRIDGE_MESSAGE_SOURCE) return
          switch (message.type) {
            case 'ready':
              setReady(true)
              return
            case 'purchase': {
              const purchase = message.data as ChatPurchase | undefined
              if (purchase) handleChatPurchase(purchase)
              return
            }
            case 'openObject': {
              const object = message.data as OpenObject | undefined
              if (object) openObject(object)
              return
            }
            case 'saveImage': {
              const image = message.data as SaveImage | undefined
              if (image) saveImage(image)
              return
            }
            case 'downloadArtifact': {
              const artifact = message.data as DownloadArtifact | undefined
              if (artifact) downloadArtifact(artifact)
              return
            }
            case 'close':
              close()
              return
            case 'conversationChanged': {
              // Keeps `/jiri?conversationId=` on the conversation in the frame, so a refresh reopens it.
              const changed = message.data as ConversationChanged | undefined
              // Kept until now: before the thread has an id a reload has nothing to resume, and the draft is all
              // that can open it again
              if (handoffRef.current === 'sent' && changed?.reason === 'created') {
                handoffRef.current = 'done'
                clearJiriDraft()
              }
              router.setParams({ conversationId: changed?.conversationId ?? undefined })
              return
            }
          }
        }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webview: {
    flex: 1,
  },
})
